import {
  Stack,
  Text,
  Heading,
  Label,
  InlineStack,
  Panel,
  PanelSection,
  Paragraph,
  FormattedDateTime,
  PanelHeader,
} from '@customerio/pluma-components/react';
import { HealthScoreGauge } from './health-score-gauge';
import { useWorkspaceHealthSimulation } from '../store';
import { useEnvHealth } from '../api/get-env-health';
import { getHealthSummary } from '../utils';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { useMemo } from 'react';
import { useWorkspaceDetails } from '@/features/masquerade-banner';
import { isEmptyString } from '@/utils/string';

interface SummaryPanelProps {
  /** Pass true when the user is on the health dashboard (enables heavy polling). */
  active?: boolean;
}

export function SummaryPanel({ active = false }: SummaryPanelProps) {
  const workspaceId = getWorkspaceIdFromCurrentUrl() ?? 0;
  const { simulate } = useWorkspaceHealthSimulation();
  const healthQuery = useEnvHealth(workspaceId, simulate, active);
  const data = healthQuery.data;
  const workspaceDetailsQuery = useWorkspaceDetails(workspaceId);
  const isIdentifiersMigrationInProgress =
    isEmptyString(workspaceDetailsQuery.data?.identifiers_migration_in_progress) === false;
  const { heading, severities, hasIssues, info } = getHealthSummary(data, isIdentifiersMigrationInProgress);

  // Prefer the backend's `last_updated_at` (when the cached response body was
  // computed) over `dataUpdatedAt` (when the FE last fetched), so the "Last
  // updated" stamp reflects actual data freshness, not request time. Falls
  // back to `dataUpdatedAt` when the backend hasn't deployed the field yet.
  const lastUpdated = useMemo(() => {
    if (data?.last_updated_at != null) return data.last_updated_at;
    if (healthQuery.dataUpdatedAt > 0) return healthQuery.dataUpdatedAt;
    return Date.now();
  }, [data?.last_updated_at, healthQuery.dataUpdatedAt]);

  return (
    <Panel>
      <PanelHeader pb="050">
        <Stack gap="200">
          <HealthScoreGauge size={84 * 0.66} active={active} />
          <Heading level="2">{heading}</Heading>
        </Stack>
      </PanelHeader>
      <PanelSection>
        <Stack gap="200">
          {info.length > 0
            ? info.map((item) => (
                <Text color="subtle" size="s" key={item}>
                  {item}
                </Text>
              ))
            : null}
          {hasIssues || severities.low > 0 ? (
            <InlineStack gap="100" wrap>
              {severities.high > 0 ? <Label color="red">{severities.high} high priority</Label> : null}
              {severities.medium > 0 ? <Label color="yellow">{severities.medium} medium</Label> : null}
              {severities.low > 0 ? <Label color="grey">{severities.low} low</Label> : null}
            </InlineStack>
          ) : null}
          <Stack gap="025">
            <Paragraph sizeVariant="xs" variant="secondary">
              Last updated{' '}
              <FormattedDateTime
                shouldUppercaseFirst={false}
                withRelativeDateFromNow={true}
                format="full_with_time"
                date={lastUpdated}
              />
            </Paragraph>
          </Stack>
        </Stack>
      </PanelSection>
    </Panel>
  );
}
